import is from 'is';
import PropTypes from 'prop-types';
import {
  useEffect, useRef, useState,
} from 'react';
import {
  Button, DetailList, TabList,
} from '@skyline-platform/alchemy';
import { useEffectOnce } from '@utils/react/hooks';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { DeletionModal } from '@components/DeletionModal/DeletionModal';
import { EditModal } from '@components/EditModal/EditModal';
import { ApiKeyForm } from '@components/FormComponents/ApiKeyForm/ApiKeyForm';
import '../Viewer.css';
import { getApiKeys } from '@utils/graphql/creators/apiKeys';
import { deleteApiKey } from '@utils/graphql/creators/apiKeys';
import { deleteContact } from '@utils/graphql/creators/contacts';
import { getLogger, filterEmptyValues } from '@utils/general';
import { ContactForm } from '@components/FormComponents/ContactForm/ContactForm';
import { contactDeleteWarning } from '../utils/Constants';
import { getContactLabelAndName } from '@pages/utils/helpers';
const logger = getLogger('src.pages.split.apikeys.ApiKeyDetails');

const getApiKey = async (key) => {
  const { query, execute } = getApiKeys(null, { userKey: key });
  query.addReturnProperties(
    'id',
    'user_key',
    'description',
    'can_read_write',
    'can_request_snap',
    'can_use_srs',
    'can_set_token_expiration',
    'can_view_private_metadata',
    'date_created',
    'zone.id',
    'zone.name',
    'contact.id',
    'contact.first_name',
    'contact.last_name',
    'contact.title',
    'contact.company_name',
    'contact.primary_phone',
    'contact.email_address'
  );

  try {
    let res = await execute();
    return res.data[0];
  } catch (error) {
    logger.error(error.message);
  }
};

const Details = (props) => {
  const keyDetails = props.details;
  const fullName = keyDetails.contact && getContactLabelAndName(keyDetails.contact).name;
  const contactLabel = keyDetails.contact && getContactLabelAndName(keyDetails.contact).label;

  const normalizedDetails = {
    ...keyDetails,
    name: keyDetails.user_key,
    contact: {
      ...keyDetails.contact,
      name: contactLabel,
    },
  };

  const keyDetailMap = {
    Description: keyDetails.description,
    Contact: fullName,
    Zone: keyDetails.zone?.name,
    'Online/Offline a Stream': keyDetails.can_read_write ? 'Yes' : 'No',
    'Can Request Snapshot': keyDetails.can_request_snap ? 'Yes' : 'No',
    'Can Record': keyDetails.can_use_srs ? 'Yes' : 'No',
    'Can Set Token Expiration': keyDetails.can_set_token_expiration ? 'Yes' : 'No',
    'Can View Secure Data': keyDetails.can_view_private_metadata ? 'Yes' : 'No',
  };

  return (
    <>
      <DetailList keyValuePair={filterEmptyValues(keyDetailMap)} />
      <div className='button-container'>
        <EditModal
          data={normalizedDetails}
          fieldName='API-key'
          FormComponent={ApiKeyForm}
          trigger={({ handleOpen }) => (
            <Button
              key={'edit-button'}
              label='Edit API Key Details'
              type='filled'
              onClick={handleOpen}
            />
          )}
        />
        <DeletionModal
          headerIcon='fa-solid fa-key'
          data={normalizedDetails}
          fieldName='API-key'
          handleDelete={deleteApiKey}
          trigger={({ handleOpen }) => (
            <Button
              key={'delete-button'}
              label='Delete API Key'
              state='error'
              type='filled'
              onClick={handleOpen}
            />
          )}
        />
      </div>
    </>
  );
};

const Contact = (props) => {
  const apiKeyContact = props.details.contact;
  const fullName = getContactLabelAndName(apiKeyContact).name;

  const normalizedContact = {
    ...apiKeyContact,
    name: fullName,
  };
  const apiKeyContactMap = {
    'Contact Name': fullName,
    Title: apiKeyContact.title,
    Company: apiKeyContact.company_name,
    'Phone number': apiKeyContact.primary_phone,
    Email: apiKeyContact.email_address,
  };
  return (
    <>
      <DetailList keyValuePair={filterEmptyValues(apiKeyContactMap)} />
      <div className='button-container'>
        <EditModal
          data={normalizedContact}
          fieldName='contact'
          FormComponent={ContactForm}
          trigger={({ handleOpen }) => (
            <Button
              key={'edit-button'}
              label='Edit Contact Details'
              type='filled'
              onClick={handleOpen}
            />
          )}
        />
        <DeletionModal
          customMessage={contactDeleteWarning}
          headerIcon='fa-solid fa-address-card'
          data={normalizedContact}
          fieldName='contact'
          handleDelete={deleteContact}
          trigger={({ handleOpen }) => (
            <Button
              key={'delete-button'}
              label='Delete Contact'
              state='error'
              type='filled'
              onClick={handleOpen}
            />
          )}
        />
      </div>
    </>
  );
};

export const ApiKeyDetails = ({ onTabDataPopulate }) => {
  const [tabData, setTabData] = useState({});
  const tabDataRef = useRef(tabData);

  const handleUpdate = async (updateMessage) => {
    if (is.empty(tabDataRef?.current)) {
      return; // Do nothing if there isn't a tab present
    }

    if (!updateMessage.detail?.payload) {
      return;
    }

    const {
      model = '',
      action = '',
      data: jsonData = '',
    } = updateMessage.detail.payload;
    const data = JSON.parse(jsonData);

    if (model === 'Apikey') {
      switch (action.toLowerCase()) {
        case 'create': { // do nothing on create, already handled by table
          return;
        }
        case 'remove': {
          setTabData(null);
          return;
        }
        case 'update': {
          const apiKey = await getApiKey(data.current.user_key);
          setTabData(apiKey);
          return;
        }
      }
    } else if (model === 'Contact') {
      switch (action.toLowerCase()) {
        case 'create': // do nothing on create
          return;
        case 'remove': // if contact removed so should the api key, double check just in case
          if (data.previous.id === tabDataRef.current.contact.id) {
            setTabData(null);
          }
          return;
        case 'update':
          if (data.current.id === tabDataRef.current.contact.id) {
            setTabData({
              ...tabDataRef.current,
              contact: data.current,
            });
          }
          return;
      }
    }
  };

  /***** SUBSCRIPTIONS *****/
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleUpdate);

    return () => {
      messageHandler.unsubscribe(handleUpdate);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  const handleAddTab = async (eventObject) => {
    let detailData = eventObject.detail;
    if (typeof detailData === 'string') { // from table, sends created id (user_key)
      detailData = await getApiKey(detailData);
    }
    setTabData(detailData);
  };

  // Handles receiving tab from AllApiKeys component
  useEffect(() => {
    tabEventEmitter.on('openTab', handleAddTab);

    onTabDataPopulate();
    tabDataRef.current = tabData;

    // Clean up the event listener on component unmount
    return () => {
      tabEventEmitter.off('openTab', handleAddTab);
    };
  }, [tabData]);

  return (
    <>
      {tabData && Object.keys(tabData).length > 0 &&
        <div className='api-key-detail-viewer'>
          <TabList
            tabs={['Details', 'Contact']}
            pages={[<Details key='details' details={tabData} />, <Contact key='contact' details={tabData} />]}
          />
        </div>
      }
    </>
  );
};

ApiKeyDetails.propTypes = {
  onTabDataPopulate: PropTypes.func.isRequired,
};

Details.propTypes = {
  details: PropTypes.array.isRequired,
};

Contact.propTypes = {
  details: PropTypes.array.isRequired,
};